/**
 * **열쇠 2종 대상 수집** — `user_key.key` · `group_key.key` (PR 7-b).
 *
 * 설계: `backend/docs/design/vault_sweep_keys/`
 *
 * 🛑 **이 모듈은 crypto 를 갖지 않는다.** `pUtils` 의 `decryptFromBase64`·`encryptAESByKey` 를
 * import 하면 **node 환경에서 죽는다** — `pUtils.js:10` 이 `env` 를 import 하고 그 체인이
 * `env → service → conf → window` 로 이어진다(0.5단계 실측:
 * `ReferenceError: window is not defined at src/conf.js:249`).
 * 변환은 `direction.convert` 에 위임하고, 어댑터(`keyCtx`)는 **`sagas.js` 가 만든다**
 * (그 파일은 이미 `pUtils` 를 import 한다).
 *
 * 🛑 **열쇠는 `record.items` 가 아니라 별도 페이로드다.** `collectFromRecord`
 * (`sweepRunner.js`)는 `record.items` 만 다루므로 열쇠는 그 함수 밖이고,
 * **`direction.hasWork` 를 자동으로 받지 못한다.** 그래서 이 함수가 명시적으로 받는다 —
 * 없으면 역방향을 돌려도 열쇠는 v2 로 남아 **이 PR 이 역방향을 포함한 이유가 무효가 된다.**
 */

/**
 * 어댑터 — `rsKeyObj`(`pUtils.js:1378`·`:1395`)와 **같은 모양**이어야 러너가 분기하지 않는다.
 *
 * @typedef {Object} KeyCtx
 * @property {'user_key'|'group_key'} kind
 * @property {string} rawMaterial                            역방향 thunk 용 원재료
 * @property {(b64: string) => Promise<string>} decrypt
 * @property {(plain: string) => Promise<string>} encrypt     서버 지시 포맷으로 (갈림길 경유)
 */

/**
 * @typedef {Object} KeySource
 * @property {'user_key'|'group_key'} table
 * @property {string} row_id   CAS 가 행을 지목한다 — 없으면 대상이 될 수 없다
 * @property {string} prev     현재 암호값. CAS 의 비교값이기도 하다
 * @property {KeyCtx} ctx
 */

/**
 * 열쇠 공급원 목록에서 교체 대상을 뽑는다.
 *
 * @param {Array<KeySource|null>} keySources `sagas.js` 가 만든다 (`user_key` 1건 + `group_key` N건)
 * @param {{convert: Function, hasWork: Function}} direction 방향 서술자
 * @returns {Promise<Array<{table: string, row_id: string, prev: string, next: string}>>}
 * @throws 복호 실패 — 🛑 **열쇠는 그 열쇠만 건너뛰지 않고 전체를 중단한다** (설계 K7).
 *         `user_key` 복호가 실패했다면 그 사용자의 개인키를 못 푼 것이고, 그러면 리소스 복호도
 *         이미 전부 실패해야 정상이다. "그 열쇠만 건너뜀"이 성립하지 않는다.
 */
export const collectKeyTargets = async (keySources, direction) => {
  const targets = []
  // 🛑 **같은 그룹이 여러 리소스에 붙으면 `group_key` 가 반복 등장한다** — `row_id` 로 dedup.
  //    서버 CAS 가 2번째를 `CONFLICT` 로 거르긴 하지만, 그 CONFLICT 는 **정상 동시성과
  //    구분되지 않아** Sentry 를 오염시킨다.
  const seen = new Set()

  // `for...of` 는 airbnb `no-restricted-syntax` 에 걸린다. 순차 await 가 필요해 인덱스 루프를 쓴다.
  const list = Array.isArray(keySources) ? keySources : []
  for (let i = 0; i < list.length; i += 1) {
    const src = list[i]
    // `row_id` 가 없으면 CAS 가 성립하지 않는다 — 구서버가 필드를 안 주는 경우다.
    if (src != null && src.row_id != null && src.prev && !seen.has(src.row_id)) {
      seen.add(src.row_id)
      if (direction.hasWork({ content: src.prev })) {
        // eslint-disable-next-line no-await-in-loop
        const next = await direction.convert(src.prev, src.ctx)
        // `null` = 대상 아님 (이미 그 방향의 포맷이거나 카나리가 꺼져 있다)
        if (next != null) {
          // 🛑 **`verify` 를 대상까지 실어 나른다** (K8). 여기서 떨어뜨리면 러너의
          //    `verifySavedKeyTargets` 가 검증할 대상을 하나도 못 찾아 **조용한 무동작**이
          //    된다 — 이 PR 이 G1 위험을 감수하는 근거가 통째로 사라진다.
          targets.push({
            table: src.table,
            row_id: src.row_id,
            prev: src.prev,
            next,
            verify: src.verify,
          })
        }
      }
    }
  }
  return targets
}

export default collectKeyTargets

/**
 * **저장한 열쇠를 다시 받아 복호해 본다** (설계 K8).
 *
 * 🛑 **재조회는 클라이언트가 API 로 다시 받는다.** 서버 안에서 확인하는 형태로 만들면
 * `get_db()` 의 일반 SELECT 가 reader 로 라우팅되어(`backend/database/database.py:78-92`)
 * **직전 커밋을 못 읽고 stale 을 읽는다.** 그러면 검증이 통과해도 아무것도 증명하지 못한다.
 * 그래서 실제 왕복은 공급원의 `verify()` 가 하고, 이 함수는 **순서**만 책임진다.
 *
 * 🛑 **순서를 고정한다** — ① 되돌리기 ② 보고 ③ 중단.
 * 반대로 하면(중단 먼저) **되돌리기 코드에 도달하지 못한다.** 사용자가 로그인할 수 있는 상태로
 * 되돌리는 것이 보고보다 급하다 (spec G1 — `user_key` 가 깨지면 되돌릴 수단 자체가 없다).
 *
 * @param {Array} targets 이번 요청에 실려 나간 대상들
 * @param {Array<string>} updatedRowIds 서버가 실제로 바꿨다고 보고한 행 id
 * @param {(t: Array) => Promise<object>} rollback 역방향 1건 요청
 * @throws 검증 실패 — 되돌리기 성공 여부와 무관하게 전체 중단 (K7)
 */
export const verifySavedKeyTargets = async ({ targets, updatedRowIds, rollback, onError }) => {
  const saved = new Set(Array.isArray(updatedRowIds) ? updatedRowIds : [])
  const list = Array.isArray(targets) ? targets : []
  for (let i = 0; i < list.length; i += 1) {
    const t = list[i]
    if (typeof t.verify === 'function' && saved.has(t.row_id)) {
      let ok = false
      try {
        // eslint-disable-next-line no-await-in-loop
        ok = (await t.verify()) === true
      } catch (err) {
        ok = false
      }
      if (!ok) {
        let restored = false
        try {
          // eslint-disable-next-line no-await-in-loop
          const r = await rollback([
            { table: t.table, row_id: t.row_id, prev: t.next, next: t.prev },
          ])
          restored = (r?.updated ?? []).length === 1
        } catch (err) {
          restored = false
        }
        // 🛑 암호값을 넘기지 않는다 (spec §8). 되돌리기까지 실패했으면 운영자가 역방향 순회를
        //    수동으로 돌려야 하므로 `restored` 를 반드시 실어 보낸다.
        if (typeof onError === 'function') {
          onError({ kind: 'key_verify_failed', table: t.table, row_id: t.row_id, restored })
        }
        throw new Error(`key_verify_failed_${t.table}`)
      }
    }
  }
}
